import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
  trackAmendSelectDateApiFailed,
  trackAmendSelectDateEligibilityFailed,
  trackAmendSelectDatePageShown,
} from '@/analytics/clevertap/amendmentSelectDatesEvents'
import { CreateCaseCallbacksResponse, postCreateCaseCallbacks } from '@/api/amend/createCaseCallbacks'
import { fetchTravellerDetailsRequest } from '@/api/amend/travellerDetails'
import type { RootState } from '@/redux/store/configureStore'
import {
  emptyAmendTravellerFulfillmentCase,
  AmendTravellerState,
  TravellerDetailsResponse,
} from '@/types/amendTraveller'
import { HTTPResponseError } from '@/types/network/common'
import { getSourceType } from '@/utils/amend/getAmendSourceType'
import {
  computeSectorAmendTier,
  deriveGdsIntlSmsFlags,
  travellerDetailsHasPromotionalOffer,
} from '@/utils/amend/travellerDetailsDerived'
import { hasBookableTravellerPayload, readEligibilityFailure } from '@/utils/amend/travellerDetailsGuards'
import { mapJourneyListFromFlights, mapPaxList } from '@/utils/amend/travellerDetailsMappers'
import { processTravellerDetailsRequestData } from '@/utils/amend/processTravellerDetailsRequestData'
const TRAVELLER_DETAILS_REJECT_UNAUTHORIZED = 'unauthorized' as const

const defaultAmendTravellerState: AmendTravellerState = {
  amendmentsLoading: true,
  notEligible: false,
  apiFailed: false,
  errorMessage: '',
  eligibilityFailureCode: '',
  userEmail: '',
  isFlexiFlyIntlBooking: false,
  ineligibleDueToOffer: false,
  eligibilityTier: null,
  isGdsIntl: false,
  isSms: false,
  travellerDetails: null,
  passengers: [],
  journeys: [],
  isRoundTripCalendar: false,
  searchAPIData: {},
  searchResults: {},
  amendCaseFlowType: '',
  amendCaseReason: '',
  caseModalLoading: false,
  fulfillmentFlag: false,
  fulfillmentCase: emptyAmendTravellerFulfillmentCase(),
  showErrorViewMsg: true,
}

const initialState: AmendTravellerState = { ...defaultAmendTravellerState }
const PROMOTIONAL_OFFER_SECTOR = 'promotional_offer_sector'
const TRAVELLER_MAPPING_FAILED = 'traveller_mapping_failed'
const FLEXIFLY_INTL_BOOKING_CODE = 'flexiflyIntlBooking'

function applyEligibilityFailure(state: AmendTravellerState, tripId: string, raw: unknown) {
  const fail = readEligibilityFailure(raw)
  state.notEligible = true
  state.errorMessage = fail.errorMessage
  state.eligibilityFailureCode = fail.clevertapKey
  state.userEmail = fail.emailId
  state.isFlexiFlyIntlBooking = fail.clevertapKey === FLEXIFLY_INTL_BOOKING_CODE
  trackAmendSelectDateEligibilityFailed(tripId, fail.errorMessage, fail.clevertapKey)
}
export const loadTravellerDetails = createAsyncThunk<
  unknown,
  string,
  { rejectValue: typeof TRAVELLER_DETAILS_REJECT_UNAUTHORIZED | 'http' }
>('amendTraveller/load', async (tripId, { rejectWithValue }) => {
  try {
    return await fetchTravellerDetailsRequest(tripId, { source: getSourceType(), platform: 'desktop' })
  } catch (event) {
    if (event instanceof HTTPResponseError && event.errorCode === 401) {
      if (typeof window !== 'undefined') {
        window.location.href = '/account/trips?src=TDCT'
      }
      return rejectWithValue(TRAVELLER_DETAILS_REJECT_UNAUTHORIZED)
    }
    return rejectWithValue('http')
  }
})
type SubmitAmendmentCreateCaseArg = { tripId: string; reqObj: Record<string, unknown> }

export const submitAmendmentCreateCase = createAsyncThunk<
  CreateCaseCallbacksResponse,
  SubmitAmendmentCreateCaseArg,
  { state: RootState; rejectValue: null }
>('amendTraveller/createCase', async ({ tripId, reqObj }, { getState, rejectWithValue }) => {
  const { amendCaseFlowType, isFlexiFlyIntlBooking } = getState().amendTraveller
  try {
    return await postCreateCaseCallbacks(tripId, reqObj, {
      caseFlowType: amendCaseFlowType,
      isFlexiFlyIntlBooking,
      getSourceType,
    })
  } catch {
    return rejectWithValue(null)
  }
})

function resetTravellerDomainState(state: AmendTravellerState) {
  state.errorMessage = ''
  state.eligibilityFailureCode = ''
  state.userEmail = ''
  state.isFlexiFlyIntlBooking = false
  state.ineligibleDueToOffer = false
  state.eligibilityTier = null
  state.isGdsIntl = false
  state.isSms = false
  state.travellerDetails = null
  state.passengers = []
  state.journeys = []
  state.isRoundTripCalendar = false
  state.searchAPIData = {}
  state.searchResults = {}
  state.amendCaseFlowType = ''
  state.amendCaseReason = ''
  state.caseModalLoading = false
  state.fulfillmentFlag = false
  state.fulfillmentCase = emptyAmendTravellerFulfillmentCase()
  state.showErrorViewMsg = true
}

const amendTravellerSlice = createSlice({
  name: 'amendTraveller',
  initialState,
  reducers: {
    setTravellerTripInvalid() {
      return {
        ...defaultAmendTravellerState,
        notEligible: true,
        amendmentsLoading: false,
        fulfillmentCase: emptyAmendTravellerFulfillmentCase(),
      }
    },
    dismissAmendmentCasePopup(state) {
      state.fulfillmentFlag = false
    },
    resetAmendCaseSession(state) {
      state.amendCaseFlowType = ''
      state.amendCaseReason = ''
      state.caseModalLoading = false
      state.fulfillmentFlag = false
      state.fulfillmentCase = emptyAmendTravellerFulfillmentCase()
      state.showErrorViewMsg = true
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loadTravellerDetails.pending, state => {
        state.amendmentsLoading = true
        state.apiFailed = false
        state.notEligible = false
        resetTravellerDomainState(state)
      })
      .addCase(loadTravellerDetails.fulfilled, (state, action) => {
        state.amendmentsLoading = false
        const tripId = action.meta.arg
        const payload = action.payload

        if (!hasBookableTravellerPayload(payload)) {
          applyEligibilityFailure(state, tripId, payload)
          return
        }

        const travellerDetails = payload as TravellerDetailsResponse

        if (travellerDetails?.isNewFlowEnabled !== true) {
          applyEligibilityFailure(state, tripId, travellerDetails)
          return
        }

        if (travellerDetailsHasPromotionalOffer(travellerDetails)) {
          state.notEligible = true
          state.ineligibleDueToOffer = true
          const offerFail = readEligibilityFailure(travellerDetails)
          trackAmendSelectDateEligibilityFailed(
            tripId,
            offerFail.errorMessage || PROMOTIONAL_OFFER_SECTOR,
            offerFail.clevertapKey || PROMOTIONAL_OFFER_SECTOR
          )
          return
        }

        try {
          state.passengers = mapPaxList(travellerDetails?.paxInfoList ?? [])
          state.journeys = mapJourneyListFromFlights(
            travellerDetails?.flightList ?? [],
            travellerDetails?.isRt ?? false
          )
        } catch {
          state.apiFailed = true
          state.notEligible = true
          state.travellerDetails = null
          state.passengers = []
          state.journeys = []
          state.isRoundTripCalendar = false
          state.eligibilityTier = null
          state.isGdsIntl = false
          state.isSms = false
          trackAmendSelectDateEligibilityFailed(tripId,TRAVELLER_MAPPING_FAILED, '')
          return
        }

        state.travellerDetails = travellerDetails
        state.isRoundTripCalendar =
          Boolean(travellerDetails?.isRt) && (travellerDetails?.flightList?.length ?? 0) === 2
        state.notEligible = false

        const { isGdsIntl, isSms } = deriveGdsIntlSmsFlags(
          travellerDetails?.flightList,
          travellerDetails?.configDaysMap
        )
        state.isGdsIntl = isGdsIntl
        state.isSms = isSms

        const tier = travellerDetails?.eligibilityTier
        const fromApi = typeof tier === 'number' && !Number.isNaN(tier) ? tier : undefined
        state.eligibilityTier =
          fromApi ??
          computeSectorAmendTier(
            travellerDetails?.flightList,
            travellerDetails?.configDaysMap,
            travellerDetails?.isFullyAmmendable
          ) ??
          null

        state.searchAPIData = processTravellerDetailsRequestData(tripId, travellerDetails)
        state.searchResults = {}
        trackAmendSelectDatePageShown(tripId, travellerDetails)
      })
      .addCase(loadTravellerDetails.rejected, (state, action) => {
        state.amendmentsLoading = false
        if (action.payload === TRAVELLER_DETAILS_REJECT_UNAUTHORIZED) {
          return
        }
        state.apiFailed = true
        state.notEligible = true
        trackAmendSelectDateApiFailed()
      })
      .addCase(submitAmendmentCreateCase.pending, (state, action) => {
        state.caseModalLoading = true
        state.amendCaseReason = String(action.meta.arg.reqObj?.reason ?? '')
      })
      .addCase(submitAmendmentCreateCase.fulfilled, (state, action) => {
        state.caseModalLoading = false
        const response = action.payload
        state.amendCaseFlowType = String(response.type ?? '')
        state.fulfillmentCase = {
          mobile: String(response.mobile ?? ''),
          email: String(response.email ?? ''),
          header: String(response.header ?? ''),
          message: String(response.message ?? ''),
        }
        state.showErrorViewMsg = false
        state.fulfillmentFlag = true
      })
      .addCase(submitAmendmentCreateCase.rejected, state => {
        state.caseModalLoading = false
        state.fulfillmentFlag = false
      })
  },
})

export const { setTravellerTripInvalid, dismissAmendmentCasePopup, resetAmendCaseSession } =
  amendTravellerSlice.actions
export const selectAmendTraveller = (s: RootState) => s.amendTraveller
export type { AmendTravellerState, AmendmentSearchAPIData, TravellerDetailsResponse } from '@/types/amendTraveller'
export default amendTravellerSlice.reducer
